/**
 * ZugPlacementEditor
 * Fabric.js placement editor for shaped stickers
 * - Artwork clipped to shape in Done mode (clean masked preview)
 * - Dim overlay outside shape in both modes (stronger in Edit, subtle in Done)
 * - Solid background fill always clipped to shape
 * - Edit/Done mode with handle visibility toggle
 * - Canvas fills full available area; mask uses min(cw,ch) as reference
 * - Transform payload for server-side compositing
 *
 * Requires: fabric.js loaded globally
 */
(function () {
  var DEFAULTS = {
    dimOutsideMask: true,
    dimOpacityEdit: 0.60,
    dimOpacityDone: 0.35,
    dimColor: "20,20,20",
    outlineColor: "#00A2F9",
    outlineWidthFactor: 0.006,
    objectBorderColor: "rgba(0,162,249,0.8)",
    objectCornerColor: "#ffffff",
    objectCornerStrokeColor: "rgba(0,0,0,0.6)",
    objectCornerSize: 13,
  };

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function degToNorm(deg) {
    var d = ((deg % 360) + 360) % 360;
    return d / 360;
  }
  function normToDeg(norm) {
    return (norm || 0) * 360;
  }

  class ZugPlacementEditor {
    constructor(opts) {
      if (!opts || !opts.canvasEl) throw new Error("ZugPlacementEditor: canvasEl required");
      if (!opts.artImageUrl) throw new Error("ZugPlacementEditor: artImageUrl required");
      if (!opts.templateId) throw new Error("ZugPlacementEditor: templateId required");
      if (!opts.templateConfig) throw new Error("ZugPlacementEditor: templateConfig required");

      this.opts = opts;
      this.templateId = opts.templateId;
      this.cfg = this._mergeConfig(opts.templateConfig);
      this.materialKey = opts.materialKey || "non_lam";
      this.onWarningChange = typeof opts.onWarningChange === "function" ? opts.onWarningChange : null;
      this.onModeChange = typeof opts.onModeChange === "function" ? opts.onModeChange : null;

      this.canvasEl = opts.canvasEl;
      this.canvas = new fabric.Canvas(this.canvasEl, {
        width: this.cfg.canvas.width,
        height: this.cfg.canvas.height,
        selection: false,
        preserveObjectStacking: true,
      });

      this.art = null;
      this.fitMode = this.cfg.defaultMode || "fit";
      this._warning = "";
      this._baseState = null;
      this._mode = "done";

      this._maskObject = null;
      this._afterRenderHandler = null;
      this._bgRect = null;
      this._maskClipPath = null;
      this._artClipPath = null;
      this._mirrorClipPath = null;

      this._magnetBack = null;
      this._magnetMirror = null;

      this._setupFabricDefaults();
      this._buildMaskClipPath();
      this._buildArtClipPath();
      this._buildMirrorClipPath();
      this._drawTemplateMask();
    }

    /* ── lifecycle ── */

    async init() {
      await this._loadArtwork(this.opts.artImageUrl);

      if (this.fitMode === "fill") this.applyFill();
      else this.applyFit();

      if (this._isMagnet()) this._buildMagnetLayers();

      this._baseState = this.getTransformPayload();
      this._wireObjectEvents();
      this._validateCoverage();
      this.setMode("done");
      this.canvas.requestRenderAll();
      return this;
    }

    _isMagnet() {
      return this.materialKey === "magnet";
    }

    destroy() {
      try {
        this._removeMagnetLayers();
        if (this._afterRenderHandler) {
          this.canvas.off("after:render", this._afterRenderHandler);
          this._afterRenderHandler = null;
        }
        this.canvas.dispose();
      } catch (e) {}
    }

    /* ── mode ── */

    getMode() {
      return this._mode;
    }

    setMode(mode) {
      if (mode !== "edit" && mode !== "done") return;
      this._mode = mode;

      if (mode === "edit") {
        if (this.art) {
          this.art.clipPath = null;
          this.art.set({ selectable: true, evented: true, hasBorders: true, hasControls: true });
          this.canvas.setActiveObject(this.art);
        }
        if (this._magnetMirror) this._magnetMirror.clipPath = null;
        this.canvas.hoverCursor = "move";
        this.canvas.moveCursor = "move";
      } else {
        this.canvas.discardActiveObject();
        if (this.art) {
          this.art.clipPath = this._artClipPath;
          this.art.set({ selectable: false, evented: false, hasBorders: false, hasControls: false });
        }
        if (this._magnetMirror) this._magnetMirror.clipPath = this._mirrorClipPath;
        this.canvas.hoverCursor = "default";
        this.canvas.moveCursor = "default";
      }

      if (this._isMagnet()) this._syncMagnetLayers();
      this.canvas.requestRenderAll();
      if (this.onModeChange) this.onModeChange(mode);
    }

    /* ── resize ── */

    resize(w, h) {
      this.cfg.canvas.width = w;
      this.cfg.canvas.height = h;
      this.canvas.setWidth(w);
      this.canvas.setHeight(h);
      if (this._maskObject) this.canvas.remove(this._maskObject);
      if (this._afterRenderHandler) {
        this.canvas.off("after:render", this._afterRenderHandler);
        this._afterRenderHandler = null;
      }
      this._buildMaskClipPath();
      this._buildArtClipPath();
      this._buildMirrorClipPath();
      this._drawTemplateMask();
      if (this._bgRect) this._updateBgRect();
      if (this._mode === "done") {
        if (this.art) this.art.clipPath = this._artClipPath;
        if (this._magnetMirror) this._magnetMirror.clipPath = this._mirrorClipPath;
      }
      if (this._isMagnet()) this._updateMagnetBack();
      if (this.fitMode === "fill") this.applyFill();
      else if (this.fitMode === "fit") this.applyFit();
      else {
        if (this._isMagnet()) this._syncMagnetLayers();
        this.canvas.requestRenderAll();
      }
    }

    /* ── update mask (live corner-radius changes etc.) ── */

    updateMask(overrides) {
      if (overrides) {
        var self = this;
        Object.keys(overrides).forEach(function(k) { self.cfg.mask[k] = overrides[k]; });
      }
      var savedTransform = this.art ? this.getTransformPayload() : null;
      var savedFitMode = this.fitMode;
      if (this._maskObject) this.canvas.remove(this._maskObject);
      if (this._afterRenderHandler) {
        this.canvas.off("after:render", this._afterRenderHandler);
        this._afterRenderHandler = null;
      }
      this._buildMaskClipPath();
      this._buildArtClipPath();
      this._buildMirrorClipPath();
      this._drawTemplateMask();
      if (this._bgRect) this._updateBgRect();
      if (this._mode === "done") {
        if (this.art) this.art.clipPath = this._artClipPath;
        if (this._magnetMirror) this._magnetMirror.clipPath = this._mirrorClipPath;
      }
      if (this._isMagnet()) this._updateMagnetBack();
      if (savedTransform) {
        this.applyTransformPayload(savedTransform);
        this.fitMode = savedFitMode;
      }
      if (this._isMagnet()) this._syncMagnetLayers();
      this.canvas.requestRenderAll();
    }

    /* ── export composed image (direct Fabric canvas snapshot) ── */

    exportComposedImage() {
      var cw = this.canvas.getWidth();
      var ch = this.canvas.getHeight();
      var objects = this.canvas.getObjects();

      console.log('[ZUG Export] canvas size:', cw + 'x' + ch);
      console.log('[ZUG Export] object count:', objects.length);
      objects.forEach(function(obj, i) {
        console.log('[ZUG Export] obj[' + i + ']:', {
          type: obj.type,
          visible: obj.visible,
          excludeFromExport: obj.excludeFromExport,
          left: Math.round(obj.left),
          top: Math.round(obj.top),
          w: Math.round(obj.getScaledWidth()),
          h: Math.round(obj.getScaledHeight()),
          clipPath: !!obj.clipPath,
          fill: obj.fill || null,
        });
      });
      if (this._bgRect) {
        console.log('[ZUG Export] bgRect:', {
          fill: this._bgRect.fill,
          visible: this._bgRect.visible,
          excludeFromExport: this._bgRect.excludeFromExport,
          clipPath: !!this._bgRect.clipPath,
        });
      } else {
        console.log('[ZUG Export] bgRect: none');
      }
      if (this.art) {
        var ctr = this.art.getCenterPoint();
        console.log('[ZUG Export] art transform:', {
          left: Math.round(ctr.x),
          top: Math.round(ctr.y),
          scaleX: this.art.scaleX,
          scaleY: this.art.scaleY,
          angle: this.art.angle,
          clipPath: !!this.art.clipPath,
        });
      }

      var wasMode = this._mode;
      var wasModeCallback = this.onModeChange;
      this.onModeChange = null;
      if (wasMode !== "done") this.setMode("done");

      var handler = this._afterRenderHandler;
      if (handler) this.canvas.off("after:render", handler);

      if (this._maskObject) this._maskObject.set({ visible: false, excludeFromExport: true });
      if (this._bgRect) this._bgRect.set({ excludeFromExport: false });
      if (this._magnetBack) this._magnetBack.set({ excludeFromExport: false });
      if (this._magnetMirror) this._magnetMirror.set({ excludeFromExport: false });
      console.log('[ZUG Export] magnet:', this._isMagnet() ? 'yes' : 'no', { back: !!this._magnetBack, mirror: !!this._magnetMirror });

      this.canvas.renderAll();
      var dataUrl = this.canvas.toDataURL({ format: "png", quality: 1 });

      console.log('[ZUG Export] exported PNG size:', Math.round(dataUrl.length / 1024) + ' KB');
      console.log('[ZUG Export] resolution:', cw + 'x' + ch);

      if (this._bgRect) this._bgRect.set({ excludeFromExport: true });
      if (this._maskObject) this._maskObject.set({ visible: true, excludeFromExport: false });
      if (handler) this.canvas.on("after:render", handler);
      this.onModeChange = wasModeCallback;
      if (wasMode !== "done") this.setMode(wasMode);
      else this.canvas.renderAll();
      return dataUrl;
    }

    /* ── background ── */

    setBackgroundColor(color) {
      if (!color || color === "transparent") {
        if (this._bgRect) {
          this.canvas.remove(this._bgRect);
          this._bgRect = null;
        }
      } else {
        if (!this._bgRect) {
          var mp = this._maskCenterPx();
          var mr = this._maskRectPx();
          this._bgRect = new fabric.Rect({
            left: mp.x,
            top: mp.y,
            originX: "center",
            originY: "center",
            width: mr.w + 4,
            height: mr.h + 4,
            fill: color,
            selectable: false,
            evented: false,
            excludeFromExport: true,
          });
          if (this._maskClipPath) this._bgRect.clipPath = this._maskClipPath;
          this.canvas.add(this._bgRect);
          this._bgRect.sendToBack();
        } else {
          this._bgRect.set({ fill: color });
        }
      }
      if (this._isMagnet()) this._stackMagnetLayers();
      this.canvas.requestRenderAll();
    }

    _updateBgRect() {
      if (!this._bgRect) return;
      var mp = this._maskCenterPx();
      var mr = this._maskRectPx();
      this._bgRect.set({
        left: mp.x,
        top: mp.y,
        width: mr.w + 4,
        height: mr.h + 4,
      });
      if (this._maskClipPath) this._bgRect.clipPath = this._maskClipPath;
      if (this._isMagnet()) {
        this._stackMagnetLayers();
      } else {
        this._bgRect.sendToBack();
      }
    }

    /* ── placement commands ── */

    applyFit() {
      if (!this.art) return;
      this.fitMode = "fit";
      this._placeContain(this.art);
      if (this._isMagnet()) this._syncMagnetLayers();
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    applyFill() {
      if (!this.art) return;
      this.fitMode = "fill";
      this._placeCover(this.art);
      if (this._isMagnet()) this._syncMagnetLayers();
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    center() {
      if (!this.art) return;
      var c = this._maskCenterPx();
      this.art.set({ left: c.x, top: c.y, originX: "center", originY: "center" });
      this.fitMode = "custom";
      if (this._isMagnet()) this._syncMagnetLayers();
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    rotate90() {
      if (!this.art) return;
      var next = (((this.art.angle || 0) + 90) % 360);
      this.art.set({ angle: next });
      this.fitMode = "custom";
      if (this._isMagnet()) this._syncMagnetLayers();
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    reset() {
      if (!this._baseState) {
        (this.cfg.defaultMode === "fill") ? this.applyFill() : this.applyFit();
        return;
      }
      this.applyTransformPayload(this._baseState);
      this.fitMode = this._baseState.fitMode || this.cfg.defaultMode || "fit";
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    /* ── state export ── */

    exportState(extraMeta) {
      if (!this.art) return null;
      var transform = this.getTransformPayload();
      if (!transform) return null;
      var bgFill = this._bgRect ? this._bgRect.fill : null;
      var st = {
        v: 2,
        templateId: this.templateId,
        mode: this._mode,
        bgMode: bgFill && bgFill !== "transparent" ? "solid" : "none",
        bgColor: bgFill && bgFill !== "transparent" ? bgFill : null,
        fitMode: transform.fitMode,
        x: transform.x,
        y: transform.y,
        scale: transform.scale,
        rotation: transform.rotation,
      };
      if (extraMeta) {
        if (extraMeta.printW != null) st.printW = extraMeta.printW;
        if (extraMeta.printH != null) st.printH = extraMeta.printH;
        if (extraMeta.shape != null) st.shape = extraMeta.shape;
        if (extraMeta.cornerRadiusIn != null) st.cornerRadiusIn = extraMeta.cornerRadiusIn;
      }
      return st;
    }

    /* ── stage rect (mask area in container-relative px) ── */

    getStageRect() {
      var mr = this._maskRectPx();
      return { x: mr.x, y: mr.y, w: mr.w, h: mr.h };
    }

    /* ── payload ── */

    getTransformPayload() {
      if (!this.art) return null;
      var cw = this.canvas.getWidth();
      var ch = this.canvas.getHeight();
      var ctr = this.art.getCenterPoint();
      var scaledW = this.art.getScaledWidth();
      return {
        v: 1,
        templateId: this.templateId,
        fitMode: this.fitMode,
        x: clamp(ctr.x / cw, 0, 1),
        y: clamp(ctr.y / ch, 0, 1),
        scale: scaledW / cw,
        rotation: degToNorm(this.art.angle || 0),
      };
    }

    applyTransformPayload(payload) {
      if (!this.art || !payload) return;
      var cw = this.canvas.getWidth();
      var ch = this.canvas.getHeight();
      var x = clamp(payload.x ?? 0.5, 0, 1) * cw;
      var y = clamp(payload.y ?? 0.5, 0, 1) * ch;
      var rot = normToDeg(payload.rotation ?? 0);
      var targetScaledW = clamp(payload.scale ?? 0.5, 0.02, 4) * cw;
      var baseW = this.art.width || 1;
      var nextScale = targetScaledW / baseW;
      this.art.set({
        originX: "center", originY: "center",
        left: x, top: y,
        scaleX: nextScale, scaleY: nextScale,
        angle: rot,
      });
      this.fitMode = payload.fitMode || "custom";
      if (this._isMagnet()) this._syncMagnetLayers();
      this._validateCoverage();
      this.canvas.requestRenderAll();
    }

    /* ── internals ── */

    _mergeConfig(templateConfig) {
      var c = templateConfig || {};
      if (!c.canvas || !c.canvas.width || !c.canvas.height) {
        throw new Error("templateConfig.canvas.width/height required");
      }
      return {
        ...c,
        _ui: { ...DEFAULTS, ...(c._ui || {}) },
      };
    }

    _setupFabricDefaults() {
      this.canvas.set({
        hoverCursor: "move",
        moveCursor: "move",
        stopContextMenu: true,
        fireRightClick: false,
        backgroundColor: "transparent",
      });
    }

    _buildMaskClipPath() {
      var mask = this.cfg.mask;
      var ref = this._minCanvasDim();
      if (mask.type === "path" && mask.d) {
        this._maskClipPath = new fabric.Path(mask.d, {
          originX: "center",
          originY: "center",
          left: 0,
          top: 0,
        });
        return;
      }
      if (mask.type === "ellipse") {
        var rxPx = (mask.rx || 0.39) * ref;
        var ryPx = (mask.ry || 0.39) * ref;
        this._maskClipPath = new fabric.Ellipse({
          rx: rxPx,
          ry: ryPx,
          originX: "center",
          originY: "center",
          left: 0,
          top: 0,
        });
      } else if (mask.type === "circle") {
        this._maskClipPath = new fabric.Circle({
          radius: mask.r * ref,
          originX: "center",
          originY: "center",
          left: 0,
          top: 0,
        });
      } else {
        var mr = this._maskRectPx();
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        this._maskClipPath = new fabric.Rect({
          width: mr.w,
          height: mr.h,
          originX: "center",
          originY: "center",
          left: 0,
          top: 0,
          rx: rx,
          ry: ry,
        });
      }
    }

    _buildArtClipPath() {
      var mask = this.cfg.mask;
      var ref = this._minCanvasDim();
      var mc = this._maskCenterPx();
      if (mask.type === "path" && mask.d) {
        this._artClipPath = new fabric.Path(mask.d, {
          absolutePositioned: true,
        });
        return;
      }
      if (mask.type === "ellipse") {
        var rxPx = (mask.rx || 0.39) * ref;
        var ryPx = (mask.ry || 0.39) * ref;
        this._artClipPath = new fabric.Ellipse({
          rx: rxPx,
          ry: ryPx,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          absolutePositioned: true,
        });
      } else if (mask.type === "circle") {
        this._artClipPath = new fabric.Circle({
          radius: mask.r * ref,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          absolutePositioned: true,
        });
      } else {
        var mr = this._maskRectPx();
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        this._artClipPath = new fabric.Rect({
          width: mr.w,
          height: mr.h,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          rx: rx,
          ry: ry,
          absolutePositioned: true,
        });
      }
    }

    _buildMirrorClipPath() {
      var mask = this.cfg.mask;
      var ref = this._minCanvasDim();
      var mc = this._maskCenterPx();
      if (mask.type === "path" && mask.d) {
        this._mirrorClipPath = new fabric.Path(mask.d, {
          absolutePositioned: true,
          flipX: true,
        });
        return;
      }
      if (mask.type === "ellipse") {
        this._mirrorClipPath = new fabric.Ellipse({
          rx: (mask.rx || 0.39) * ref,
          ry: (mask.ry || 0.39) * ref,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          absolutePositioned: true,
          flipX: true,
        });
      } else if (mask.type === "circle") {
        this._mirrorClipPath = new fabric.Circle({
          radius: mask.r * ref,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          absolutePositioned: true,
          flipX: true,
        });
      } else {
        var mr = this._maskRectPx();
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        this._mirrorClipPath = new fabric.Rect({
          width: mr.w,
          height: mr.h,
          left: mc.x,
          top: mc.y,
          originX: "center",
          originY: "center",
          rx: rx,
          ry: ry,
          absolutePositioned: true,
          flipX: true,
        });
      }
    }

    _drawTemplateMask() {
      var mask = this.cfg.mask;
      if (!mask || !mask.type) throw new Error("templateConfig.mask required");
      var ref = this._minCanvasDim();
      var dynStroke = Math.max(1.5, Math.round(ref * (this.cfg._ui.outlineWidthFactor || 0.006)));
      var mc = this._maskCenterPx();

      var outline;
      if (mask.type === "path" && mask.d) {
        outline = new fabric.Path(mask.d, {
          fill: "transparent",
          stroke: this.cfg._ui.outlineColor,
          strokeWidth: dynStroke,
          selectable: false,
          evented: false,
        });
      } else if (mask.type === "ellipse") {
        var erx = (mask.rx || 0.39) * ref;
        var ery = (mask.ry || 0.39) * ref;
        outline = new fabric.Ellipse({
          left: mc.x - erx,
          top: mc.y - ery,
          rx: erx,
          ry: ery,
          fill: "transparent",
          stroke: this.cfg._ui.outlineColor,
          strokeWidth: dynStroke,
          selectable: false,
          evented: false,
        });
      } else if (mask.type === "circle") {
        var r = mask.r * ref;
        outline = new fabric.Circle({
          left: mc.x - r,
          top:  mc.y - r,
          radius: r,
          fill: "transparent",
          stroke: this.cfg._ui.outlineColor,
          strokeWidth: dynStroke,
          selectable: false,
          evented: false,
        });
      } else if (mask.type === "rect" || mask.type === "rounded-rect") {
        var px = this._maskRectPx();
        var rx = mask.type === "rounded-rect" ? (mask.rx || 0) * ref : 0;
        var ry = mask.type === "rounded-rect" ? (mask.ry || 0) * ref : 0;
        outline = new fabric.Rect({
          left: px.x,
          top: px.y,
          width: px.w,
          height: px.h,
          rx: rx,
          ry: ry,
          fill: "transparent",
          stroke: this.cfg._ui.outlineColor,
          strokeWidth: dynStroke,
          selectable: false,
          evented: false,
        });
      } else if (mask.type !== "path") {
        throw new Error("Unknown mask.type: " + mask.type);
      }

      outline.shadow = new fabric.Shadow({
        color: 'rgba(0,0,0,0.45)',
        blur: 6,
        offsetX: 0,
        offsetY: 0,
        nonScaling: true
      });
      outline.excludeFromExport = true;
      this._maskObject = outline;
      outline.zugRole = 'coasterMaskOverlay';
      this.canvas.add(outline);
      outline.bringToFront();

      if (this.cfg._ui.dimOutsideMask) {
        this._afterRenderHandler = () => this._drawDimOverlay();
        this.canvas.on("after:render", this._afterRenderHandler);
      }
    }

    _drawDimOverlay() {
      var opacity = this._mode === "edit"
        ? this.cfg._ui.dimOpacityEdit
        : (this.cfg._ui.dimOpacityDone || 0);
      if (opacity <= 0) return;

      var ctx = this.canvas.getContext();
      var cw = this.canvas.getWidth();
      var ch = this.canvas.getHeight();
      var ref = this._minCanvasDim();
      var mask = this.cfg.mask;
      var dimColor = this.cfg._ui.dimColor || "20,20,20";
      var mc = this._maskCenterPx();

      ctx.save();
      ctx.fillStyle = "rgba(" + dimColor + "," + opacity + ")";

      if (mask.type === "path" && mask.d) {
        var frame = new Path2D();
        frame.rect(0, 0, cw, ch);
        frame.addPath(new Path2D(mask.d));
        ctx.fill(frame, "evenodd");
        ctx.restore();
        return;
      }

      ctx.beginPath();
      ctx.rect(0, 0, cw, ch);

      if (mask.type === "ellipse") {
        var erx = (mask.rx || 0.39) * ref;
        var ery = (mask.ry || 0.39) * ref;
        ctx.moveTo(mc.x + erx, mc.y);
        ctx.ellipse(mc.x, mc.y, erx, ery, 0, 0, Math.PI * 2);
      } else if (mask.type === "circle") {
        var r = mask.r * ref;
        ctx.moveTo(mc.x + r, mc.y);
        ctx.arc(mc.x, mc.y, r, 0, Math.PI * 2);
      } else {
        var px = this._maskRectPx();
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        if (rx > 0 || ry > 0) {
          this._roundedRectPath(ctx, px.x, px.y, px.w, px.h, rx, ry);
        } else {
          ctx.rect(px.x, px.y, px.w, px.h);
        }
      }

      ctx.fill("evenodd");
      ctx.restore();
    }

    _roundedRectPath(ctx, x, y, w, h, rx, ry) {
      var r = Math.min(rx, ry);
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.arcTo(x + w, y, x + w, y + r, r);
      ctx.lineTo(x + w, y + h - r);
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
      ctx.lineTo(x + r, y + h);
      ctx.arcTo(x, y + h, x, y + h - r, r);
      ctx.lineTo(x, y + r);
      ctx.arcTo(x, y, x + r, y, r);
      ctx.closePath();
    }

    /* ── transparent trim ── */

    _trimTransparentPadding(htmlImg, padding) {
      padding = padding || 20;
      var oc = document.createElement("canvas");
      var w = htmlImg.naturalWidth || htmlImg.width;
      var h = htmlImg.naturalHeight || htmlImg.height;
      oc.width = w;
      oc.height = h;
      var ctx = oc.getContext("2d");
      ctx.drawImage(htmlImg, 0, 0, w, h);

      var data;
      try {
        data = ctx.getImageData(0, 0, w, h);
      } catch (e) {
        console.warn("[ZUG Placement] getImageData failed (CORS), skipping trim", e);
        return null;
      }
      var pixels = data.data;

      var minX = w, minY = h, maxX = -1, maxY = -1;
      for (var y2 = 0; y2 < h; y2++) {
        for (var x2 = 0; x2 < w; x2++) {
          if (pixels[(y2 * w + x2) * 4 + 3] > 0) {
            if (x2 < minX) minX = x2;
            if (x2 > maxX) maxX = x2;
            if (y2 < minY) minY = y2;
            if (y2 > maxY) maxY = y2;
          }
        }
      }

      if (maxX < 0 || maxY < 0) {
        console.warn("[ZUG Placement] artwork is fully transparent, skipping trim");
        return null;
      }

      var bx = Math.max(0, minX - padding);
      var by = Math.max(0, minY - padding);
      var bx2 = Math.min(w - 1, maxX + padding);
      var by2 = Math.min(h - 1, maxY + padding);
      var bw = bx2 - bx + 1;
      var bh = by2 - by + 1;

      if (bw >= w && bh >= h) {
        console.log("[ZUG Placement] no transparent padding to trim", { w: w, h: h });
        return null;
      }

      var tc = document.createElement("canvas");
      tc.width = bw;
      tc.height = bh;
      tc.getContext("2d").drawImage(oc, bx, by, bw, bh, 0, 0, bw, bh);

      console.log("[ZUG Placement] trimmed artwork", {
        original: w + "x" + h,
        bbox: { x: bx, y: by, w: bw, h: bh },
        trimmed: bw + "x" + bh,
        padding: padding
      });

      return tc.toDataURL("image/png");
    }

    /* ── artwork loading ── */

    async _loadArtwork(url) {
      var self = this;
      return new Promise(function(resolve, reject) {
        var rawImg = new Image();
        rawImg.crossOrigin = "anonymous";
        rawImg.onload = function () {
          var trimmedUrl = self._trimTransparentPadding(rawImg, 20);
          var loadUrl = trimmedUrl || url;

          fabric.Image.fromURL(
            loadUrl,
            function onLoad(img) {
              if (!img || !img.width || !img.height) {
                console.error("[ZUG Placement] Image loaded but has zero dimensions", { url: loadUrl });
                reject(new Error("Preview image could not load in editor (CORS). Falling back."));
                return;
              }

              console.log("[ZUG Placement] Image loaded", { url: url, trimmed: !!trimmedUrl, w: img.width, h: img.height });

              img.set({
                originX: "center",
                originY: "center",
                selectable: true,
                evented: true,
                hasBorders: true,
                hasControls: true,
                lockUniScaling: true,
                cornerColor: self.cfg._ui.objectCornerColor,
                cornerStrokeColor: self.cfg._ui.objectCornerStrokeColor,
                borderColor: self.cfg._ui.objectBorderColor,
                cornerSize: self.cfg._ui.objectCornerSize,
                transparentCorners: false,
                padding: 2,
                borderScaleFactor: 1,
                minScaleLimit: 0.05,
                cornerStyle: "circle",
              });

              img.setControlsVisibility({ mtr: true });

              self.art = img;
              self.canvas.add(img);
              img.bringToFront();
              if (self._maskObject) self._maskObject.bringToFront();

              var mc = self._maskCenterPx();
              img.set({ left: mc.x, top: mc.y });

              self.canvas.setActiveObject(img);
              resolve(img);
            },
            function onError(err) {
              console.error("[ZUG Placement] Image failed to load in Fabric (likely CORS)", { url: loadUrl, err: err });
              reject(new Error("Preview image could not load in editor (CORS). Falling back."));
            },
            { crossOrigin: "anonymous" }
          );
        };
        rawImg.onerror = function (err) {
          console.error("[ZUG Placement] Raw image failed to load (CORS)", { url: url, err: err });
          reject(new Error("Preview image could not load in editor (CORS). Falling back."));
        };
        rawImg.src = url;
      });
    }

    /* ── events ── */

    _wireObjectEvents() {
      var self = this;
      var markCustom = function() {
        if (self.fitMode !== "custom") self.fitMode = "custom";
      };
      var syncMagnet = function() {
        if (self._isMagnet()) self._syncMagnetLayers();
      };
      var maxScale = 5;

      this.canvas.on("object:moving", function() { markCustom(); syncMagnet(); });
      this.canvas.on("object:scaling", function(e) {
        markCustom();
        var obj = e.target;
        if (obj) {
          var s = Math.max(obj.scaleX, obj.scaleY);
          if (s > maxScale) obj.set({ scaleX: maxScale, scaleY: maxScale });
        }
        syncMagnet();
      });
      this.canvas.on("object:rotating", function() { markCustom(); syncMagnet(); });
      this.canvas.on("object:modified", function() {
        markCustom();
        syncMagnet();
        self._validateCoverage();
      });
    }

    /* ── magnet depth layers ── */

    _magnetGradient(w, h) {
      return new fabric.Gradient({
        type: 'linear',
        gradientUnits: 'pixels',
        coords: { x1: -w / 2, y1: -h / 2, x2: w / 2, y2: h / 2 },
        colorStops: [
          { offset: 0, color: '#514a3f' },
          { offset: 1, color: '#30291f' }
        ]
      });
    }

    _buildMagnetLayers() {
      if (!this.art || !this._isMagnet()) return;
      this._removeMagnetLayers();

      var mc = this._maskCenterPx();
      var mr = this._maskRectPx();
      var cw = this.canvas.getWidth();
      var depthPx = Math.min(cw * 0.07, 28);

      var mask = this.cfg.mask;
      var ref = this._minCanvasDim();
      var backShape;
      var magnetStyle = { stroke: "#474033", strokeWidth: 1, strokeUniform: true, paintFirst: "fill" };

      if (mask.type === "ellipse") {
        var erx = (mask.rx || 0.39) * ref;
        var ery = (mask.ry || 0.39) * ref;
        backShape = new fabric.Ellipse(Object.assign({
          rx: erx, ry: ery,
          left: mc.x - depthPx,
          top: mc.y - depthPx,
          originX: "center", originY: "center",
          fill: this._magnetGradient(erx * 2, ery * 2),
          selectable: false, evented: false,
          excludeFromExport: false,
        }, magnetStyle));
      } else if (mask.type === "circle") {
        var r = mask.r * ref;
        backShape = new fabric.Circle(Object.assign({
          radius: r,
          left: mc.x - depthPx,
          top: mc.y - depthPx,
          originX: "center", originY: "center",
          fill: this._magnetGradient(r * 2, r * 2),
          selectable: false, evented: false,
          excludeFromExport: false,
        }, magnetStyle));
      } else {
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        backShape = new fabric.Rect(Object.assign({
          width: mr.w, height: mr.h,
          left: mc.x - depthPx,
          top: mc.y - depthPx,
          originX: "center", originY: "center",
          rx: rx, ry: ry,
          fill: this._magnetGradient(mr.w, mr.h),
          selectable: false, evented: false,
          excludeFromExport: false,
        }, magnetStyle));
      }

      this._magnetBack = backShape;
      this.canvas.add(backShape);

      var mirrorImg = new fabric.Image(this.art.getElement(), {
        originX: "center", originY: "center",
        left: this.art.left,
        top: this.art.top,
        scaleX: this.art.scaleX,
        scaleY: this.art.scaleY,
        angle: this.art.angle,
        flipX: true,
        opacity: 0.25,
        selectable: false,
        evented: false,
        excludeFromExport: false,
      });
      if (this._mode === "done" && this._mirrorClipPath) {
        mirrorImg.clipPath = this._mirrorClipPath;
      }

      this._magnetMirror = mirrorImg;
      this.canvas.add(mirrorImg);

      this._stackMagnetLayers();
      console.log("[ZUG Placement] magnet layers built, depthPx=" + depthPx);
    }

    _removeMagnetLayers() {
      if (this._magnetBack) {
        this.canvas.remove(this._magnetBack);
        this._magnetBack = null;
      }
      if (this._magnetMirror) {
        this.canvas.remove(this._magnetMirror);
        this._magnetMirror = null;
      }
    }

    _updateMagnetBack() {
      if (!this._magnetBack) return;
      var mc = this._maskCenterPx();
      var mr = this._maskRectPx();
      var cw = this.canvas.getWidth();
      var depthPx = Math.min(cw * 0.07, 28);
      var mask = this.cfg.mask;
      var ref = this._minCanvasDim();

      this._magnetBack.set({
        left: mc.x - depthPx,
        top: mc.y - depthPx,
      });

      if (mask.type === "ellipse") {
        var erx = (mask.rx || 0.39) * ref;
        var ery = (mask.ry || 0.39) * ref;
        this._magnetBack.set({ rx: erx, ry: ery, fill: this._magnetGradient(erx * 2, ery * 2) });
      } else if (mask.type === "circle") {
        var cr = mask.r * ref;
        this._magnetBack.set({ radius: cr, fill: this._magnetGradient(cr * 2, cr * 2) });
      } else {
        var rx = (mask.type === "rounded-rect") ? (mask.rx || 0) * ref : 0;
        var ry = (mask.type === "rounded-rect") ? (mask.ry || 0) * ref : 0;
        this._magnetBack.set({ width: mr.w, height: mr.h, rx: rx, ry: ry, fill: this._magnetGradient(mr.w, mr.h) });
      }
    }

    _syncMagnetLayers() {
      if (!this.art) return;
      if (this._magnetMirror) {
        this._magnetMirror.set({
          left: this.art.left,
          top: this.art.top,
          scaleX: this.art.scaleX,
          scaleY: this.art.scaleY,
          angle: this.art.angle,
          flipX: true,
        });
      }
      this._stackMagnetLayers();
    }

    _stackMagnetLayers() {
      var idx = 0;
      if (this._magnetBack) this._magnetBack.moveTo(idx++);
      if (this._magnetMirror) this._magnetMirror.moveTo(idx++);
      if (this._bgRect) this._bgRect.moveTo(idx++);
      if (this.art) this.art.moveTo(idx++);
      if (this._maskObject) this._maskObject.bringToFront();
      if (this._magnetMirror) {
        this._magnetMirror.set({ visible: !this._bgRect });
      }
    }

    /* ── geometry helpers ── */

    _maskCenterPx() {
      return { x: this.canvas.getWidth() / 2, y: this.canvas.getHeight() / 2 };
    }

    _maskRectPx() {
      var cw = this.canvas.getWidth();
      var ch = this.canvas.getHeight();
      var ref = this._minCanvasDim();
      var mask = this.cfg.mask;
      if (mask.type === "path" && mask.bbox) {
        return { x: mask.bbox.x, y: mask.bbox.y, w: mask.bbox.w, h: mask.bbox.h };
      }
      if (mask.type === "ellipse") {
        var erx = (mask.rx || 0.39) * ref;
        var ery = (mask.ry || 0.39) * ref;
        return { x: cw / 2 - erx, y: ch / 2 - ery, w: erx * 2, h: ery * 2 };
      }
      if (mask.type === "circle") {
        var r = mask.r * ref;
        return { x: cw / 2 - r, y: ch / 2 - r, w: r * 2, h: r * 2 };
      }
      var w = (mask.w ?? 0.8) * ref;
      var h = (mask.h ?? 0.8) * ref;
      return { x: (cw - w) / 2, y: (ch - h) / 2, w: w, h: h };
    }

    _minCanvasDim() {
      return Math.min(this.canvas.getWidth(), this.canvas.getHeight());
    }

    _placeContain(img) {
      var target = this._maskRectPx();
      var cx = target.x + target.w / 2;
      var cy = target.y + target.h / 2;
      var curAngle = img.angle || 0;
      var isRotated = (Math.round(curAngle) % 180) !== 0;
      var effW = isRotated ? img.height : img.width;
      var effH = isRotated ? img.width : img.height;
      var scale = Math.min(target.w / effW, target.h / effH);
      img.set({ originX: "center", originY: "center", left: cx, top: cy, scaleX: scale, scaleY: scale });
    }

    _placeCover(img) {
      var target = this._maskRectPx();
      var cx = target.x + target.w / 2;
      var cy = target.y + target.h / 2;
      var curAngle = img.angle || 0;
      var isRotated = (Math.round(curAngle) % 180) !== 0;
      var effW = isRotated ? img.height : img.width;
      var effH = isRotated ? img.width : img.height;
      var scale = Math.max(target.w / effW, target.h / effH);
      img.set({ originX: "center", originY: "center", left: cx, top: cy, scaleX: scale, scaleY: scale });
    }

    _validateCoverage() {
      if (!this.cfg.enforceCoverage) {
        this._setWarning("");
        return;
      }
      if (!this.art) return;
      var bleed = this.cfg.bleed || 0;
      var maskPx = this._maskRectPx();
      var pad = bleed * this._minCanvasDim();
      var required = { x: maskPx.x - pad, y: maskPx.y - pad, w: maskPx.w + pad * 2, h: maskPx.h + pad * 2 };
      var b = this.art.getBoundingRect(true, true);
      var covers = b.left <= required.x && b.top <= required.y &&
        (b.left + b.width) >= (required.x + required.w) &&
        (b.top + b.height) >= (required.y + required.h);
      this._setWarning(covers ? "" : "Design must cover the full print area (including bleed). Tap Fill or scale up.");
    }

    _setWarning(msg) {
      if (msg === this._warning) return;
      this._warning = msg;
      if (this.onWarningChange) this.onWarningChange(msg);
    }
  }

  window.ZugPlacementEditor = ZugPlacementEditor;
})();
